/**
 * BUSCAR A ALGUIEN EN EL CUADRO Y QUE EL CUADRO SE MUEVA HASTA ÉL.
 *
 * QUÉ PROBLEMA RESUELVE. Un cuadro de 128 son siete columnas y sesenta y
 * cuatro llaves en la primera ronda: un dibujo de dos metros de ancho por
 * cinco de alto. Encontrarse a uno mismo ahí es recorrerlo con el dedo, y a
 * la tercera pasada uno ya no sabe por dónde iba. Con esto se escriben tres
 * letras y el cuadro se mueve solo hasta la llave.
 *
 * LLEVA AL PARTIDO MÁS AVANZADO, no al primero. Quien busca a alguien quiere
 * saber DÓNDE VA, no dónde empezó: si ya jugó tres rondas, el que importa es
 * el de la cuarta. Todas sus llaves quedan marcadas igual, para poder seguir
 * el camino hacia atrás con el ojo.
 *
 * NO FILTRA EL CUADRO, LO SEÑALA. Un cuadro filtrado deja de ser un cuadro:
 * lo que se lee ahí es contra quién juega cada uno, y eso son los de al lado.
 *
 * BUSCA POR LAS DOS PARTES DEL NOMBRE. En el cuadro cada llave muestra el
 * apodo arriba y el nombre de pila abajo —«Beto Céspedes» / «Bryan»— porque en
 * un cuadro hay tres Betos y el apodo no alcanza para saber cuál es cuál. Pero
 * la búsqueda miraba SOLO el apodo: escribir «Bryan» —el nombre que se está
 * leyendo en la pantalla, a dos centímetros del buscador— contestaba «Nadie con
 * ese nombre en este cuadro», que además de inútil es mentira.
 *
 * SIN TILDES, SIN MAYÚSCULAS Y SIN NADA QUE NO SEA LETRA: nadie escribe
 * «Muñoz» con la ñ cuando está apurado buscándose en un torneo, ni pone el
 * guion de «Jean-Pierre». Lo resuelve `searchKey`, del contrato, que es la
 * misma función que usa el resto de la aplicación: acá había una copia
 * parecida pero no igual.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { searchKey, type DrawableEntry, type DrawableMatch } from '@tennisup/contracts';

/** Cuántas letras hacen falta antes de mostrar nombres. */
const MÍNIMO = 1;
/** Cuántos nombres se ofrecen. Más que esto es una lista que hay que leer. */
const TOPE = 8;

export function BracketSearch({
  matches,
  destacado,
  onPick,
}: {
  matches: readonly DrawableMatch[];
  /** El id de inscripción marcado ahora, o `null`. */
  destacado: string | null;
  /** Se llama con el id de inscripción, o con `null` para desmarcar. */
  onPick: (entryId: string | null) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [texto, setTexto] = useState('');

  /**
   * Cada persona UNA vez, aunque aparezca en cinco llaves.
   *
   * Un `Map` por id de inscripción: la misma persona inscrita en dos
   * categorías tiene DOS inscripciones y son dos entradas distintas de la
   * lista, que es lo correcto — juegan dos cuadros.
   */
  const gente = useMemo(() => {
    const porId = new Map<string, DrawableEntry>();
    for (const match of matches) {
      for (const entry of [match.home, match.away]) {
        if (entry !== null && !porId.has(entry.id)) porId.set(entry.id, entry);
      }
    }
    return [...porId.values()].sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [matches]);

  const buscado = searchKey(texto);
  const encontrados =
    buscado.length < MÍNIMO
      ? []
      : gente.filter((e) => nombreBuscable(e).includes(buscado)).slice(0, TOPE);

  const marcado = destacado === null ? null : (gente.find((e) => e.id === destacado) ?? null);

  return (
    <div className="bracket-search">
      <label className="visually-hidden" htmlFor="bracket-search-input">
        {t('bracket.findPlayer')}
      </label>
      <input
        id="bracket-search-input"
        type="search"
        autoComplete="off"
        placeholder={t('bracket.findPlayer')}
        value={texto}
        onChange={(event) => setTexto(event.target.value)}
      />

      {/* MARCADO Y CÓMO QUITARLO, en la misma línea. Una marca que no se sabe
          apagar deja el cuadro con un resaltado que estorba para siempre. */}
      {marcado !== null && (
        <p className="bracket-search-on">
          <span>{t('bracket.showing', { name: marcado.displayName })}</span>{' '}
          <button
            type="button"
            className="link"
            onClick={() => {
              onPick(null);
              setTexto('');
            }}
          >
            {t('bracket.clearFind')}
          </button>
        </p>
      )}

      {buscado.length >= MÍNIMO && encontrados.length === 0 && (
        <p className="help bracket-search-none">{t('bracket.noPlayer')}</p>
      )}

      {encontrados.length > 0 && (
        <ul className="bracket-search-hits">
          {encontrados.map((entry) => (
            <li key={entry.id}>
              <button
                type="button"
                className="link"
                onClick={() => {
                  onPick(entry.id);
                  setTexto('');
                }}
              >
                {entry.displayName}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * EL PARTIDO MÁS AVANZADO DE ESA PERSONA, que es al que hay que llevar la
 * pantalla. `null` si no juega ninguno.
 */
export function partidoMásAvanzado(
  matches: readonly DrawableMatch[],
  entryId: string | null,
): string | null {
  if (entryId === null) return null;
  const suyos = matches.filter((m) => m.home?.id === entryId || m.away?.id === entryId);
  if (suyos.length === 0) return null;
  return suyos.reduce((mejor, m) => (m.round > mejor.round ? m : mejor)).id;
}

/**
 * Lleva la pantalla hasta ese partido.
 *
 * `scrollIntoView` PUEDE NO EXISTIR: jsdom no lo implementa, y esta función
 * la llaman efectos que corren en las pruebas. Sin la guarda, cada prueba que
 * marque a alguien revienta por una animación que no le importa.
 *
 * `center` en los dos ejes: el cuadro se desplaza de lado adentro de su caja
 * y la página de arriba abajo. Dejar la llave pegada a un borde es dejarla
 * medio tapada por la barra de arriba o por el borde de la caja.
 */
export function llevarAlPartido(matchId: string | null): void {
  if (matchId === null) return;
  const el = document.querySelector(`[data-match="${matchId}"]`);
  if (el === null) return;
  el.scrollIntoView?.({ block: 'center', inline: 'center', behavior: 'smooth' });
}

/**
 * TODO LO QUE SE VE DE ESA PERSONA EN LA LLAVE, junto y buscable.
 *
 * El apodo Y el nombre de pila, que es exactamente lo que la llave muestra: si
 * está escrito en la pantalla, se tiene que poder buscar. `firstName` es
 * opcional en `DrawableEntry` —el cuadro público no siempre lo trae— y ahí
 * queda solo el apodo, que es lo único que hay.
 */
function nombreBuscable(entry: DrawableEntry): string {
  return searchKey(`${entry.displayName} ${entry.firstName ?? ''}`);
}
